import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { useCampaignPostCallMessages } from '@features/Admin/Campaign/hooks/useCampaignPostCallMessages';
import {
  PostCallMessageConditionDraft,
  PostCallMessageDraft,
  PostCallMessageValidationErrors,
  createEmptyPostCallCondition,
  validatePostCallMessageDraft,
} from '@features/Admin/Campaign/utils/post-call-message-mapper';

import PostCallMessageCard from './PostCallMessageCard';
import PostCallMessageDeleteDialog from './PostCallMessageDeleteDialog';
import PostCallMessagePopup from './PostCallMessagePopup';

interface PostCallMessageSectionProps {
  campaignId: string;
  campaignName: string;
  companyName: string;
  isDisabled: boolean;
}

const PostCallMessageSection: React.FC<PostCallMessageSectionProps> = ({
  campaignId,
  campaignName,
  companyName,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    messages,
    analysisFields,
    analysisFieldNames,
    isLoading,
    isSaving,
    isDeleting,
    createDraft,
    saveMessage,
    deleteMessage,
    moveMessage,
  } = useCampaignPostCallMessages(campaignId);

  const [draft, setDraft] = useState<PostCallMessageDraft | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [errors, setErrors] = useState<PostCallMessageValidationErrors>({});
  const [deleteCandidate, setDeleteCandidate] = useState<PostCallMessageDraft | null>(null);

  const isBusy = isSaving || isDeleting;

  const unknownFieldsByMessage = useMemo(() => {
    if (analysisFieldNames.length === 0) {
      return new Map<string, string[]>();
    }

    const knownFields = new Set(analysisFieldNames);

    return new Map(
      messages.map((message) => [
        message.id ?? '',
        message.conditions
          .map((condition) => condition.field)
          .filter((field) => field && !knownFields.has(field)),
      ]),
    );
  }, [analysisFieldNames, messages]);

  const showError = (error: unknown, fallbackKey: string) => {
    toast.error(getApiErrorMessage(error, t(fallbackKey)));
  };

  const handleCreate = () => {
    setDraft(createDraft());
    setErrors({});
    setIsPopupOpen(true);
  };

  const handleEdit = (message: PostCallMessageDraft) => {
    setDraft({ ...message, conditions: message.conditions.map((item) => ({ ...item })) });
    setErrors({});
    setIsPopupOpen(true);
  };

  const handleCancel = () => {
    setDraft(null);
    setIsPopupOpen(false);
    setErrors({});
  };

  const handleDraftChange = <K extends keyof PostCallMessageDraft>(
    key: K,
    value: PostCallMessageDraft[K],
  ) => {
    setDraft((previous) => (previous ? { ...previous, [key]: value } : previous));
    setErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  const updateConditions = (
    update: (conditions: PostCallMessageConditionDraft[]) => PostCallMessageConditionDraft[],
  ) => {
    setDraft((previous) =>
      previous ? { ...previous, conditions: update(previous.conditions) } : previous,
    );
    setErrors((previous) => ({ ...previous, conditions: undefined }));
  };

  const handleAddCondition = () => {
    updateConditions((conditions) => [...conditions, createEmptyPostCallCondition()]);
  };

  const handleRemoveCondition = (index: number) => {
    updateConditions((conditions) => conditions.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleConditionChange = (index: number, condition: PostCallMessageConditionDraft) => {
    updateConditions((conditions) =>
      conditions.map((item, itemIndex) => (itemIndex === index ? condition : item)),
    );
  };

  const handleSave = async () => {
    if (!draft) {
      return;
    }

    const validation = validatePostCallMessageDraft(draft);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      await saveMessage(draft);
      handleCancel();
    } catch (error) {
      showError(error, 'campaignPage.postCallMessages.messages.saveError');
    }
  };

  const handleToggle = async (message: PostCallMessageDraft, checked: boolean) => {
    try {
      await saveMessage({ ...message, isEnabled: checked });
    } catch (error) {
      showError(error, 'campaignPage.postCallMessages.messages.saveError');
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    try {
      await moveMessage(index, direction);
    } catch (error) {
      showError(error, 'campaignPage.postCallMessages.messages.saveError');
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate?.id) {
      return;
    }

    try {
      await deleteMessage(deleteCandidate.id);
      if (draft?.id === deleteCandidate.id) {
        handleCancel();
      }
      setDeleteCandidate(null);
    } catch (error) {
      showError(error, 'campaignPage.postCallMessages.messages.deleteError');
    }
  };

  return (
    <>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography fontSize="18px" fontWeight={600}>
            {t('campaignPage.postCallMessages.title')}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={isDisabled || isBusy}>
            {t('campaignPage.postCallMessages.addMessage')}
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('campaignPage.postCallMessages.subtitle')}
        </Typography>

        {analysisFields.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('campaignPage.postCallMessages.noAnalysisSchema')}
          </Alert>
        ) : null}

        {isLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : messages.length > 0 ? (
          <Box>
            {messages.map((message, index) => (
              <PostCallMessageCard
                key={message.id}
                message={message}
                index={index}
                isFirst={index === 0}
                isLast={index === messages.length - 1}
                disabled={isDisabled}
                busy={isBusy}
                unknownFields={unknownFieldsByMessage.get(message.id ?? '') ?? []}
                onEdit={handleEdit}
                onDelete={setDeleteCandidate}
                onToggle={handleToggle}
                onMove={handleMove}
              />
            ))}
          </Box>
        ) : (
          <BorderedGrid
            sx={{
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: theme.palette.grey[50],
            }}>
            <Typography fontSize="15px" fontWeight={600} sx={{ mb: 1 }}>
              {t('campaignPage.postCallMessages.emptyTitle')}
            </Typography>
            <Typography color="text.secondary">
              {t('campaignPage.postCallMessages.emptyDescription')}
            </Typography>
          </BorderedGrid>
        )}
      </Box>

      <PostCallMessagePopup
        open={isPopupOpen}
        draft={draft}
        errors={errors}
        analysisFields={analysisFields}
        campaignName={campaignName}
        companyName={companyName}
        disabled={isDisabled}
        isLoading={isSaving}
        onChange={handleDraftChange}
        onAddCondition={handleAddCondition}
        onRemoveCondition={handleRemoveCondition}
        onConditionChange={handleConditionChange}
        onClose={handleCancel}
        onSave={handleSave}
      />

      <PostCallMessageDeleteDialog
        open={Boolean(deleteCandidate)}
        position={deleteCandidate ? messages.indexOf(deleteCandidate) + 1 : undefined}
        isLoading={isDeleting}
        onClose={() => setDeleteCandidate(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default PostCallMessageSection;
